import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import MovieFilterBar, { type MovieFilters } from "@/components/feature/MovieFilterBar";
import SortTabs, { type SortKey } from "@/components/feature/SortTabs";
import { filterMovies, paginate, sortMovies } from "@/utils/movieQuery";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 12;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  useSeo({
    title: "饥渴的岳中文字幕 - 电影分类与筛选",
    description: "浏览并筛选《饥渴的岳》中文字幕电影资源，查找符合条件的影视作品。",
    keywords: "饥渴的岳分类,中文字幕电影,影片筛选,影视资源分类,中文字幕BD,饥渴的岳合集",
  });

  const filters: MovieFilters = {
    region: searchParams.get("region") || "全部",
    year: searchParams.get("year") || "全部",
    version: searchParams.get("v") || "全部",
    genre: searchParams.get("genre") || "全部",
  };
  const sort = (searchParams.get("sort") as SortKey) || "latest";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const update = (patch: Record<string, string>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "全部") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    if (resetPage) next.delete("page");
    setSearchParams(next);
  };

  const filtered = useMemo(() => {
    const result = filterMovies(movies, filters);
    return sortMovies(result, sort);
  }, [filters.region, filters.year, filters.version, filters.genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = paginate(filtered, currentPage, PAGE_SIZE);

  const handleFilters = (next: MovieFilters) => {
    update({ region: next.region, year: next.year, v: next.version, genre: next.genre });
  };

  const resetAll = () => {
    setSearchParams(new URLSearchParams());
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 font-label mb-6 flex-wrap">
        <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-200">分类</span>
      </nav>

      <header className="mb-8 md:mb-10">
        <h1 className="font-heading text-foreground-50 text-2xl md:text-4xl font-bold mb-3">
          《饥渴的岳》中文字幕资源分类
        </h1>
        <p className="text-sm md:text-base text-foreground-400 font-label max-w-3xl leading-relaxed">
          按上映地区、年份、字幕版本与影片类型组合筛选，快速定位你想要的《饥渴的岳》系列中文字幕资源。
        </p>
      </header>

      <div className="lg:hidden mb-4">
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="w-full flex items-center justify-center gap-2 h-11 rounded-md border border-background-700 text-foreground-100 font-label text-sm cursor-pointer whitespace-nowrap"
        >
          <i className="ri-filter-3-line text-accent-500" />
          {showFilters ? "收起筛选" : "展开筛选条件"}
        </button>
      </div>

      <div className={`${showFilters ? "block" : "hidden"} lg:block mb-6`}>
        <MovieFilterBar
          filters={filters}
          onChange={handleFilters}
          onReset={resetAll}
          resultCount={filtered.length}
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <p className="text-sm text-foreground-400 font-label">
          筛选结果 <span className="text-accent-500 font-semibold">{filtered.length}</span> 部
        </p>
        <SortTabs value={sort} onChange={(key) => update({ sort: key === "latest" ? "" : key })} />
      </div>

      {pageItems.length > 0 ? (
        <>
          <MovieGrid movies={pageItems} />
          <Pagination
            current={currentPage}
            totalPages={totalPages}
            onChange={(p) => update({ page: String(p) }, false)}
          />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="w-16 h-16 rounded-full bg-background-900 text-foreground-500 flex items-center justify-center text-3xl mb-5">
            <i className="ri-inbox-line" />
          </span>
          <h3 className="font-heading text-foreground-100 text-lg font-semibold mb-2">
            没有找到符合条件的影片
          </h3>
          <p className="text-sm text-foreground-400 font-label mb-6 max-w-md">
            试试放宽筛选条件，或重置后重新浏览全部《饥渴的岳》中文字幕资源。
          </p>
          <button
            type="button"
            onClick={resetAll}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}